/** Epsilon for comparing floating-point values in Yjs capture and repair. */
export const Y_NUMBER_EPSILON = 1e-6;

/** Deep equality check with epsilon tolerance for numbers. Compares values
 *  recursively: plain objects ignore key order and undefined-valued keys,
 *  arrays must match element-wise and in order (a hole reads as `undefined`,
 *  whichever side holds it), numbers use epsilon tolerance (via Math.abs, or
 *  Object.is for NaN and infinities), and everything else — a Date, a Map, a
 *  class instance — uses ===. */
export function valuesEqualWithin(a: unknown, b: unknown, eps: number = Y_NUMBER_EPSILON): boolean {
    // Handle numbers: epsilon tolerance
    if (typeof a === 'number' && typeof b === 'number') {
        return Math.abs(a - b) <= eps || Object.is(a, b);
    }

    // Handle arrays: same length, element-wise equality. An index loop, not
    // `every`, which skips holes in whichever array it is called on.
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) {
            if (!valuesEqualWithin(a[i], b[i], eps)) return false;
        }
        return true;
    }

    // Handle plain objects: same keys (ignoring undefined), recursive comparison
    if (isPlainObject(a) && isPlainObject(b)) {
        const keysA = Object.keys(a).filter((k) => a[k] !== undefined);
        const keysB = Object.keys(b).filter((k) => b[k] !== undefined);

        if (keysA.length !== keysB.length) return false;
        if (!keysA.every((k) => keysB.includes(k))) return false;

        return keysA.every((k) => valuesEqualWithin(a[k], b[k], eps));
    }

    // Everything else: strict equality
    return a === b;
}

/** A plain object: an object literal, parsed JSON, or `Object.create(null)`.
 *  Arrays, Dates, Maps and other class instances are not — comparing their
 *  own keys would call two different Dates, or two Maps, equal. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== 'object') return false;
    const proto: unknown = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}
